/**
 * Assistant API wrapper
 *
 * This module provides the client‑side integration layer for all Instant Assistant
 * operations, including:
 *  - Session creation (new/existing)
 *  - Message sending and normalized message handling
 *  - Task‑draft and feedback submission
 *  - Voice session lifecycle
 *  - Server‑sent event (SSE) streaming for messages, queue updates, status events,
 *    and session‑level events
 *
 * The functions here wrap backend API endpoints, normalize payloads where needed,
 * and ensure consistent request semantics (authentication, JSON encoding, error
 * surfaces). UI components should rely on these helpers rather than calling the
 * backend directly.
 */
import { buildAgentApiUrl } from "@/lib/api-config";
import { applyRefreshedTokenFromResponse, getApiAccessToken } from "@/lib/api-client";
import {
  normalizeAssistantMessage,
  type AssistantMessagePayload,
} from "@/lib/instant-assistant-normalizer";
import type {
  InstantAssistantContextHydrationStatus,
  InstantAssistantContextSummary,
  InstantAssistantDiscoveryUpdatePayload,
  InstantAssistantFeedbackPayload,
  InstantAssistantMessage,
  InstantAssistantMessageCreatePayload,
  InstantAssistantMessageResponse,
  InstantAssistantQueueState,
  InstantAssistantSessionCreatePayload,
  InstantAssistantSessionResponse,
  InstantAssistantStatusEvent,
  InstantAssistantTaskDraftPayload,
  InstantAssistantToolCallEvent,
  InstantAssistantVoiceSessionRequestPayload,
  InstantAssistantVoiceSessionResponse,
} from "@/types/assistant";

export type AssistantStreamHandlers = {
  onMessage?: (message: InstantAssistantMessage) => void;
  onQueue?: (queue: InstantAssistantQueueState) => void;
  onStatus?: (status: InstantAssistantStatusEvent) => void;
  onToolCall?: (event: InstantAssistantToolCallEvent) => void;
  onSessionEvent?: (payload: unknown) => void;
  onError?: (error: unknown) => void;
};

type VoiceSessionEndPayload = {
  reason?: string;
  notes?: string;
};

const assistantBasePath = "/api/assistant";

function withAssistantBase(path: string) {
  return `${assistantBasePath}${path.startsWith("/") ? path : `/${path}`}`;
}

const voiceBasePath = "/api/voice";

function withVoiceBase(path: string) {
  return `${voiceBasePath}${path.startsWith("/") ? path : `/${path}`}`;
}

const EMPTY_GUID = "00000000-0000-0000-0000-000000000000";

function isUsableSessionId(sessionId: unknown): sessionId is string {
  return typeof sessionId === "string" && sessionId.length > 0 && sessionId !== EMPTY_GUID;
}

async function postAssistantSession(
  path: string,
  payload: InstantAssistantSessionCreatePayload,
  signal?: AbortSignal,
): Promise<InstantAssistantSessionResponse> {
  const response = await requestJson<InstantAssistantSessionResponse>(withAssistantBase(path), {
    method: "POST",
    body: JSON.stringify(payload),
    signal,
  });

  const transcript = (response.transcript ?? []).map((message) =>
    normalizeAssistantMessage(message as AssistantMessagePayload),
  );

  return {
    ...response,
    transcript,
  };
}

export async function createAssistantSession(
  payload: InstantAssistantSessionCreatePayload,
  signal?: AbortSignal,
): Promise<InstantAssistantSessionResponse> {
  /**
   * Creates a new assistant session.
   * Payload includes optional initial message or context.
   * Returns normalized transcript and session metadata.
   *
   * If the resume path returns a poisoned record (sessionId missing or
   * Guid.Empty — happens when an existing row's id failed to hydrate
   * server-side), retry once via `/sessions/new` to force a fresh
   * session. Without this, every subsequent message POST would target
   * `/sessions/00000000-.../messages` and the agent enqueue step would
   * fail with "SessionId is required".
   */
  const response = await postAssistantSession("/sessions", payload, signal);
  if (isUsableSessionId(response.sessionId)) {
    return response;
  }
  return postAssistantSession("/sessions/new", payload, signal);
}

export function createAssistantSessionNew(
  payload: InstantAssistantSessionCreatePayload,
  signal?: AbortSignal,
): Promise<InstantAssistantSessionResponse> {
  /**
   * Creates a fresh assistant session explicitly via the `/sessions/new` endpoint.
   */
  return postAssistantSession("/sessions/new", payload, signal);
}

export async function sendAssistantMessage(
  sessionId: string,
  payload: InstantAssistantMessageCreatePayload,
  signal?: AbortSignal,
): Promise<InstantAssistantMessageResponse | undefined> {
  /**
   * Sends a message within an existing assistant session. The relay
   * controller returns 202 Accepted with an empty body — the operator
   * echo + assistant reply both arrive via the SSE stream, so this
   * function resolves with `undefined` on the new overlay path. The
   * legacy panel still typed it as a populated response; callers must
   * handle the undefined case (or stay on the SSE-driven path the
   * overlay uses).
   */
  const response = await requestJson<InstantAssistantMessageResponse | undefined>(
    withAssistantBase(`/sessions/${sessionId}/messages`),
    { method: "POST", body: JSON.stringify(payload), signal },
  );
  if (!response) return undefined;
  return {
    ...response,
    message: normalizeAssistantMessage(response.message as AssistantMessagePayload),
  };
}

export async function submitAssistantTaskDraft(
  sessionId: string,
  payload: InstantAssistantTaskDraftPayload,
  signal?: AbortSignal,
): Promise<void> {
  /**
   * Submits a task-draft action tied to the current assistant session.
   */
  await requestJson<void>(withAssistantBase(`/sessions/${sessionId}/actions/task-draft`), {
    method: "POST",
    body: JSON.stringify(payload),
    signal,
  });
}

export async function submitAssistantFeedback(
  sessionId: string,
  payload: InstantAssistantFeedbackPayload,
  signal?: AbortSignal,
): Promise<void> {
  /**
   * Submits user feedback for the assistant session.
   */
  await requestJson<void>(withAssistantBase(`/sessions/${sessionId}/feedback`), {
    method: "POST",
    body: JSON.stringify(payload),
    signal,
  });
}

export async function createAssistantVoiceSession(
  sessionId: string,
  payload: InstantAssistantVoiceSessionRequestPayload,
  signal?: AbortSignal,
): Promise<InstantAssistantVoiceSessionResponse> {
  /**
   * Starts a voice session for speech input/output associated with an assistant session.
   */
  return requestJson<InstantAssistantVoiceSessionResponse>(
    withAssistantBase(`/sessions/${sessionId}/voice`),
    {
      method: "POST",
      body: JSON.stringify(payload),
      signal,
    },
  );
}

export async function endVoiceSession(
  voiceSessionId: string,
  namespaceId: string,
  payload?: VoiceSessionEndPayload,
  signal?: AbortSignal,
): Promise<void> {
  /**
   * Ends an ongoing voice session. Accepts optional structured end payload.
   * The gateway routes the session lookup by namespace, so the namespace the
   * session was created in is required.
   */
  await requestJson<void>(
    withVoiceBase(
      `/sessions/${voiceSessionId}:end?namespaceId=${encodeURIComponent(namespaceId)}`,
    ),
    {
      method: "POST",
      body: JSON.stringify(payload ?? {}),
      signal,
    },
  );
}

export function openAssistantStream(
  sessionId: string,
  handlers: AssistantStreamHandlers,
  options?: { since?: Date },
): () => void {
  /**
   * Opens a server-sent events (SSE) stream for the given assistant session.
   * Emits:
   *  - message: normalized assistant messages
   *  - queue: queue-state updates
   *  - status: assistant status events
   *  - session: generic session-level notifications
   * Returns a function that closes the underlying EventSource.
   */
  const params = new URLSearchParams();
  if (options?.since) {
    params.set("since", options.since.toISOString());
  }
  const token = getApiAccessToken();
  if (token) {
    params.set("access_token", token);
  }

  const streamUrl = buildAgentApiUrl(
    withAssistantBase(
      `/sessions/${sessionId}/stream${params.size ? `?${params.toString()}` : ""}`,
    ),
  );

  const eventSource = new EventSource(streamUrl);

  eventSource.addEventListener("message", (event) => {
    try {
      const parsed = JSON.parse(event.data) as AssistantMessagePayload;
      handlers.onMessage?.(normalizeAssistantMessage(parsed));
    } catch (error) {
      handlers.onError?.(error);
    }
  });

  eventSource.addEventListener("queue", (event) => {
    try {
      const parsed = JSON.parse(event.data);
      handlers.onQueue?.(mapQueueFromApi(parsed));
    } catch (error) {
      handlers.onError?.(error);
    }
  });

  eventSource.addEventListener("status", (event) => {
    try {
      const parsed = JSON.parse(event.data);
      handlers.onStatus?.(mapStatusFromApi(parsed));
    } catch (error) {
      handlers.onError?.(error);
    }
  });

  eventSource.addEventListener("tool_call", (event) => {
    try {
      const parsed = JSON.parse(event.data);
      handlers.onToolCall?.(mapToolCallFromApi(parsed));
    } catch (error) {
      handlers.onError?.(error);
    }
  });

  eventSource.addEventListener("session", (event) => {
    try {
      handlers.onSessionEvent?.(JSON.parse(event.data));
    } catch (error) {
      handlers.onError?.(error);
    }
  });

  eventSource.onerror = (error) => {
    handlers.onError?.(error);
  };

  return () => {
    eventSource.close();
  };
}

async function requestJson<T>(path: string, init?: RequestInit): Promise<T> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(init?.headers as Record<string, string> ?? {}),
  };
  const token = getApiAccessToken();
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  const response = await fetch(buildAgentApiUrl(path), {
    ...init,
    headers,
  });

  applyRefreshedTokenFromResponse(response);

  if (!response.ok) {
    const raw = await response.text();
    let message = raw;
    if (raw) {
      try {
        const parsed = JSON.parse(raw) as { error?: string; message?: string };
        message = parsed.error ?? parsed.message ?? raw;
      } catch {
        // not JSON — fall through with the raw text
      }
    }
    throw new Error(message || `Request to ${path} failed with ${response.status}`);
  }

  // 202 Accepted (assistant message/action POSTs) and 204 No Content both
  // ship empty bodies. Calling response.json() on an empty body throws
  // "Unexpected end of JSON input" — return undefined for these statuses.
  if (response.status === 204 || response.status === 202) {
    return undefined as T;
  }

  return (await response.json()) as T;
}

type AssistantQueuePayload = {
  depth?: number;
  remainingCapacity?: number;
  nextAvailableIn?: string | null;
  activeEntryId?: string | null;
};

type AssistantStatusPayload = {
  kind?: string;
  messageId?: string | null;
  queueEntryId?: string | null;
  availableAtUtc?: string | null;
  detail?: string | null;
  contextSummary?: ContextSummaryPayload | null;
};

type ContextSummaryPayload = {
  totalCards?: number;
  authoritativeCards?: number;
  types?: string[];
  hasSelection?: boolean;
  hydrationStatus?: string;
  failureReason?: string | null;
};

function mapQueueFromApi(payload: AssistantQueuePayload): InstantAssistantQueueState {
  return {
    depth: payload.depth ?? 0,
    remainingCapacity: payload.remainingCapacity ?? 0,
    nextAvailableInMs: payload.nextAvailableIn
      ? parseDuration(payload.nextAvailableIn)
      : null,
    activeEntryId: payload.activeEntryId ?? null,
  };
}

function mapStatusFromApi(payload: AssistantStatusPayload): InstantAssistantStatusEvent {
  const normalizedKind = (payload.kind ?? "idle").toLowerCase();
  const allowedKinds = new Set(["thinking", "slow_path", "queued", "idle", "error"]);
  const kind = allowedKinds.has(normalizedKind) ? (normalizedKind as InstantAssistantStatusEvent["kind"]) : "idle";
  return {
    kind,
    messageId: payload.messageId ?? null,
    queueEntryId: payload.queueEntryId ?? null,
    availableAtUtc: payload.availableAtUtc ?? null,
    detail: payload.detail ?? null,
    contextSummary: mapContextSummaryFromApi(payload.contextSummary),
  };
}

type AssistantToolCallPayload = {
  eventId?: string;
  sessionId?: string;
  tool?: string;
  label?: string;
  target?: string | null;
  kind?: string;
  ok?: boolean;
  errorMessage?: string | null;
  timestampUtc?: string;
};

function mapToolCallFromApi(payload: AssistantToolCallPayload): InstantAssistantToolCallEvent {
  return {
    eventId: payload.eventId ?? "",
    sessionId: payload.sessionId ?? "",
    tool: payload.tool ?? "",
    label: payload.label ?? payload.tool ?? "Tool call",
    target: payload.target ?? null,
    kind: payload.kind ?? "action",
    ok: payload.ok ?? true,
    errorMessage: payload.errorMessage ?? null,
    timestampUtc: payload.timestampUtc ?? new Date().toISOString(),
  };
}

function mapContextSummaryFromApi(payload: ContextSummaryPayload | null | undefined): InstantAssistantContextSummary | null {
  if (!payload) {
    return null;
  }

  const hydrationStatus = (payload.hydrationStatus ?? "none").toLowerCase();
  const allowedStatuses: InstantAssistantContextHydrationStatus[] = ["none", "pending", "success", "partial", "failed"];
  const mappedStatus = allowedStatuses.includes(hydrationStatus as InstantAssistantContextHydrationStatus)
    ? (hydrationStatus as InstantAssistantContextHydrationStatus)
    : "none";

  return {
    totalCards: payload.totalCards ?? 0,
    authoritativeCards: payload.authoritativeCards ?? 0,
    types: payload.types ?? [],
    hasSelection: Boolean(payload.hasSelection),
    hydrationStatus: mappedStatus,
    failureReason: payload.failureReason ?? null,
  };
}

function parseDuration(value: string): number {
  // Expecting HH:MM:SS.FFFFFF
  const match = value.match(/^(\d+):(\d+):(\d+)(?:\.(\d+))?$/);
  if (!match) {
    return 0;
  }

  const hours = Number(match[1] ?? 0);
  const minutes = Number(match[2] ?? 0);
  const seconds = Number(match[3] ?? 0);
  const fractional = match[4] ? Number(`0.${match[4]}`) : 0;

  return Math.round(((hours * 3600 + minutes * 60 + seconds) + fractional) * 1000);
}

export function buildDiscoveryUpdate(payload: InstantAssistantDiscoveryUpdatePayload | undefined) {
  if (!payload) {
    return undefined;
  }
  return payload;
}
